import React from "react";
import {
  CONTROL_LEVELS,
  GROUP_OVERRIDE_SETTINGS,
  STATUS_PILL,
  formatNetworkDateTime,
  getControlLevels,
} from "utils/network";

const Pill = ({ children, className }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${className}`}
  >
    {children}
  </span>
);

const Block = ({ title, hint, children }) => (
  <section className="min-w-0">
    <div className="flex items-baseline justify-between gap-3 mb-2.5">
      <h5 className="text-[11px] uppercase tracking-[0.06em] font-semibold text-black">
        {title}
      </h5>
      {hint ? <span className="text-xs text-darkgray">{hint}</span> : null}
    </div>
    {children}
  </section>
);

const Panel = ({ children }) => (
  <div className="border border-mediumgray rounded-md overflow-hidden bg-white">
    {children}
  </div>
);

const rowBorder = "border-t border-mediumgray first:border-t-0";

const DlRow = ({ label, value }) => (
  <div className={`flex items-baseline gap-4 px-4 py-2.5 ${rowBorder}`}>
    <span className="text-[13px] text-darkgray flex-shrink-0">{label}</span>
    <span className="ml-auto min-w-0 text-right break-words">
      {value ? (
        <span className="text-black">{value}</span>
      ) : (
        <span className="text-darkgray">Not set</span>
      )}
    </span>
  </div>
);

const StateRow = ({ name, description, children }) => (
  <div className={`flex items-start gap-3.5 px-4 py-3 ${rowBorder}`}>
    <div className="min-w-0">
      <div className="font-medium text-black">{name}</div>
      {description ? (
        <div className="text-xs text-darkgray mt-0.5">{description}</div>
      ) : null}
    </div>
    <div className="ml-auto flex-shrink-0 pt-px">{children}</div>
  </div>
);

const Stat = ({ label, value, suffix }) => (
  <div className="px-4 py-3.5 border-l border-mediumgray first:border-l-0">
    <div className="text-[11px] uppercase tracking-[0.06em] font-medium text-darkgray">
      {label}
    </div>
    <div className="mt-1 text-xl font-semibold text-black tabular-nums leading-tight">
      {value}
      {suffix ? (
        <span className="text-[13px] font-normal text-darkgray"> {suffix}</span>
      ) : null}
    </div>
  </div>
);

const levelClass = (level) =>
  level === "group"
    ? "bg-blue-100 text-blue-800"
    : level === "division"
      ? "bg-amber-100 text-amber-800"
      : "bg-lightgray text-darkergray";

export default function DivisionView({ division }) {
  if (!division) return null;

  const controlLevels = getControlLevels(division);
  const governs = division.governs || [];
  const emails = division.operationalEmails || {};
  const brandAssets = division.brandAssets || [];
  const complianceRules = division.compliance?.rules || [];
  const restrictedDays = division.compliance?.restrictedDays || [];
  const templates = division.templates || {};
  const socialTemplates = templates.social || [];

  // Only what this division actually narrowed. Inheriting is the norm, so
  // listing every setting would bury the two that were changed.
  const narrowed = GROUP_OVERRIDE_SETTINGS.filter((setting) =>
    (division.controlledBy || []).some((entry) => entry.path === setting.key),
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="border border-mediumgray rounded-md overflow-hidden">
        <div className="flex flex-wrap items-center gap-5 px-6 py-6 bg-lightergray">
          <div className="min-w-0 flex-1">
            <div className="text-[22px] font-bold leading-tight tracking-[-0.01em] text-balance">
              {division.name}
            </div>
            <div className="mt-1.5 text-[13px] text-darkgray">
              {division.networkId?.name || "No network"}
            </div>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium capitalize ${
              STATUS_PILL[division.status || "active"] || STATUS_PILL.active
            }`}
          >
            {division.status || "active"}
          </span>
        </div>

        {/* The jurisdictions are what the division IS — a division governing
            none is inert, so it reads as unfinished rather than as empty. */}
        <div className="border-t border-mediumgray bg-white px-6 py-3.5">
          {governs.length ? (
            <div className="flex flex-wrap gap-1.5">
              {governs.map((code) => (
                <Pill key={code} className="bg-blue-100 text-blue-800 font-mono">
                  {code}
                </Pill>
              ))}
            </div>
          ) : (
            <span className="text-sm text-red-500">
              Governs no jurisdiction — nothing will resolve through it
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 border border-mediumgray rounded-md overflow-hidden bg-white">
        <Stat label="Jurisdictions" value={governs.length} />
        <Stat
          label="Narrowed"
          value={narrowed.length}
          suffix={`of ${GROUP_OVERRIDE_SETTINGS.length}`}
        />
        <Stat label="Brand assets" value={brandAssets.length} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Block
          title="Overrides"
          hint={narrowed.length ? `${narrowed.length} narrowed` : "All inherited"}
        >
          <Panel>
            {GROUP_OVERRIDE_SETTINGS.map((setting) => {
              const isNarrowed = narrowed.some((n) => n.key === setting.key);
              return (
                <StateRow
                  key={setting.key}
                  name={setting.name}
                  description={setting.description}
                >
                  {isNarrowed ? (
                    <Pill className={levelClass(controlLevels[setting.key])}>
                      {CONTROL_LEVELS.find(
                        (l) => l.value === controlLevels[setting.key],
                      )?.label || controlLevels[setting.key]}
                    </Pill>
                  ) : (
                    <Pill className="bg-lightgray text-darkergray">
                      Inherits network
                    </Pill>
                  )}
                </StateRow>
              );
            })}
          </Panel>
        </Block>

        <Block title="Operational emails">
          <Panel>
            <DlRow label="Settlements" value={emails.settlements} />
            <DlRow label="Concierge" value={emails.concierge} />
            <DlRow label="Accounts" value={emails.accounts} />
            <DlRow label="Admin" value={emails.admin} />
            <DlRow label="Key events" value={emails.keyEvents} />
          </Panel>
        </Block>

        <Block title="Templates">
          <Panel>
            <DlRow
              label="Prelist"
              value={templates.prelist ? "Set" : null}
            />
            <DlRow
              label="Post list"
              value={templates.postList ? "Set" : null}
            />
            <DlRow
              label="Social"
              value={
                socialTemplates.length
                  ? `${socialTemplates.length} post type${socialTemplates.length === 1 ? "" : "s"}`
                  : null
              }
            />
            <DlRow
              label="Community post"
              value={
                templates.communitySocialPost?.enabled ? "Enabled" : null
              }
            />
          </Panel>
        </Block>

        <Block title="Compliance">
          <Panel>
            <DlRow
              label="Rules"
              value={
                complianceRules.length
                  ? `${complianceRules.filter((r) => r.enabled).length} of ${complianceRules.length} enabled`
                  : null
              }
            />
            <DlRow
              label="Restricted days"
              value={restrictedDays.length ? restrictedDays.join(", ") : null}
            />
          </Panel>
        </Block>
      </div>

      <Block title="Record">
        <Panel>
          <DlRow label="Created" value={formatNetworkDateTime(division.createdAt)} />
          <DlRow label="Updated" value={formatNetworkDateTime(division.updatedAt)} />
        </Panel>
      </Block>
    </div>
  );
}
